import { useState, useMemo } from 'react'
import {
  Cloud,
  Server,
  AlertCircle,
  CheckCircle,
  TrendingUp,
  Zap,
  HardDrive,
  Cpu,
  Activity,
  Plus,
  X,
  Trash2,
  Edit2,
  Lightbulb,
  DollarSign,
  Shield,
  Lock,
  BarChart3,
  Eye,
  AlertTriangle,
  CheckSquare,
  Wifi,
  Database,
} from 'lucide-react'
import { useCloudProjectsStore } from '../store/cloudProjectsStore'
import { Modal, FormInput, FormSelect, FormTextarea } from '../components/common/CRUDControls'

export default function CloudMonitorPage() {
  const cloudProviders = useCloudProjectsStore((state) => state.cloudProviders)
  const addCloudProject = useCloudProjectsStore((state) => state.addCloudProject)
  const updateCloudProject = useCloudProjectsStore((state) => state.updateCloudProject)
  const deleteCloudProject = useCloudProjectsStore((state) => state.deleteCloudProject)
  const generateAIInsights = useCloudProjectsStore((state) => state.generateAIInsights)
  const getCostOptimizations = useCloudProjectsStore((state) => state.getCostOptimizations)
  const getAllProjects = useCloudProjectsStore((state) => state.getAllProjects)

  const [selectedProvider, setSelectedProvider] = useState(null)
  const [selectedProject, setSelectedProject] = useState(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [activeTab, setActiveTab] = useState('overview') // overview, security, compliance, threats, inventory, risk

  const [formData, setFormData] = useState({
    name: '',
    status: 'running',
    uptime: 99.9,
    instances: 1,
    cost: 100,
    resources: { cpu: 50, memory: 50, storage: 50 },
  })

  // Mock security vulnerabilities
  const securityVulnerabilities = [
    { id: 1, name: 'Unencrypted S3 bucket', severity: 'critical', provider: 'AWS', count: 2, recommendation: 'Enable encryption at rest' },
    { id: 2, name: 'Open security group', severity: 'high', provider: 'AWS', count: 1, recommendation: 'Restrict inbound rules' },
    { id: 3, name: 'IAM access key rotation overdue', severity: 'high', provider: 'AWS', count: 3, recommendation: 'Rotate access keys' },
    { id: 4, name: 'Outdated OS versions', severity: 'medium', provider: 'Azure', count: 5, recommendation: 'Apply latest patches' },
    { id: 5, name: 'No Multi-Factor Authentication', severity: 'high', provider: 'GCP', count: 4, recommendation: 'Enable MFA' },
  ]

  // Mock compliance data
  const complianceStatus = [
    { standard: 'SOC 2 Type II', status: 'compliant', lastAudit: '2026-04-15', nextAudit: '2027-04-15', progress: 100 },
    { standard: 'HIPAA', status: 'compliant', lastAudit: '2026-03-01', nextAudit: '2027-03-01', progress: 100 },
    { standard: 'PCI-DSS', status: 'partial', lastAudit: '2026-02-01', nextAudit: '2026-08-01', progress: 75 },
    { standard: 'GDPR', status: 'compliant', lastAudit: '2026-01-15', nextAudit: '2027-01-15', progress: 100 },
    { standard: 'ISO 27001', status: 'in-progress', lastAudit: '2025-12-01', nextAudit: '2026-06-01', progress: 60 },
  ]

  // Mock threat detection data
  const recentThreats = [
    { id: 1, type: 'Brute Force Attack', severity: 'critical', timestamp: '2 hours ago', source: 'IP: 192.168.1.100', status: 'blocked' },
    { id: 2, type: 'Suspicious API Activity', severity: 'high', timestamp: '4 hours ago', source: 'Unknown region', status: 'investigating' },
    { id: 3, type: 'Privilege Escalation Attempt', severity: 'critical', timestamp: '6 hours ago', source: 'IAM service', status: 'blocked' },
    { id: 4, type: 'Data Exfiltration Detected', severity: 'high', timestamp: '8 hours ago', source: 'Outbound traffic', status: 'quarantined' },
    { id: 5, type: 'Configuration Drift', severity: 'medium', timestamp: '1 day ago', source: 'Auto-scan', status: 'resolved' },
  ]

  // Mock cloud inventory
  const cloudInventory = [
    { type: 'EC2 Instances', count: 24, provider: 'AWS', status: 'running', cost: '$1,200/mo' },
    { type: 'RDS Databases', count: 8, provider: 'AWS', status: 'active', cost: '$800/mo' },
    { type: 'S3 Buckets', count: 45, provider: 'AWS', status: 'secure', cost: '$300/mo' },
    { type: 'VMs', count: 18, provider: 'Azure', status: 'running', cost: '$900/mo' },
    { type: 'SQL Databases', count: 6, provider: 'Azure', status: 'active', cost: '$400/mo' },
    { type: 'Compute Instances', count: 12, provider: 'GCP', status: 'running', cost: '$600/mo' },
    { type: 'Cloud SQL', count: 4, provider: 'GCP', status: 'active', cost: '$200/mo' },
    { type: 'Storage Buckets', count: 32, provider: 'GCP', status: 'secure', cost: '$150/mo' },
  ]

  // Calculate risk score
  const calculateRiskScore = () => {
    const criticalIssues = securityVulnerabilities.filter(v => v.severity === 'critical').length * 10
    const highIssues = securityVulnerabilities.filter(v => v.severity === 'high').length * 5
    const mediumIssues = securityVulnerabilities.filter(v => v.severity === 'medium').length * 2
    const nonCompliantStandards = complianceStatus.filter(c => c.status !== 'compliant').length * 3
    const activeThreats = recentThreats.filter(t => t.status === 'investigating' || t.status === 'blocked').length * 4

    const totalScore = Math.min(100, Math.max(0, 100 - (criticalIssues + highIssues + mediumIssues + nonCompliantStandards + activeThreats)))
    return totalScore
  }

  const riskScore = calculateRiskScore()

  // Calculate totals
  const stats = useMemo(() => {
    const allProjects = getAllProjects()
    return {
      totalCost: allProjects.reduce((sum, p) => sum + p.cost, 0),
      totalInstances: allProjects.reduce((sum, p) => sum + p.instances, 0),
      avgUptime: (allProjects.reduce((sum, p) => sum + p.uptime, 0) / allProjects.length).toFixed(2),
      providers: cloudProviders.length,
      runningProjects: allProjects.filter((p) => p.status === 'running').length,
      totalProjects: allProjects.length,
      criticalVulnerabilities: securityVulnerabilities.filter(v => v.severity === 'critical').length,
      complianceScore: (complianceStatus.filter(c => c.status === 'compliant').length / complianceStatus.length * 100).toFixed(0),
    }
  }, [cloudProviders])

  const selectedProviderData = selectedProvider
    ? cloudProviders.find((p) => p.id === selectedProvider)
    : null

  const selectedProjectData = selectedProject && selectedProviderData
    ? selectedProviderData.projects.find((p) => p.id === selectedProject)
    : null

  const aiInsights = selectedProjectData
    ? generateAIInsights(selectedProvider, selectedProject)
    : []

  const costOptimizations = getCostOptimizations()

  const filteredProjects = selectedProviderData
    ? selectedProviderData.projects.filter((p) =>
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.id.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : []

  const handleAddProject = () => {
    setFormData({
      name: '',
      status: 'running',
      uptime: 99.9,
      instances: 1,
      cost: 100,
      resources: { cpu: 50, memory: 50, storage: 50 },
    })
    setShowAddModal(true)
  }

  const handleSaveProject = () => {
    if (formData.name && selectedProvider) {
      if (selectedProject && showEditModal) {
        updateCloudProject(selectedProvider, selectedProject, formData)
      } else {
        addCloudProject(selectedProvider, formData)
      }
      setShowAddModal(false)
      setShowEditModal(false)
      setSelectedProject(null)
    }
  }

  const handleEditProject = () => {
    if (selectedProjectData) {
      setFormData(selectedProjectData)
      setShowEditModal(true)
      setShowAddModal(true)
    }
  }

  const handleDeleteProject = () => {
    if (selectedProject && selectedProvider) {
      deleteCloudProject(selectedProvider, selectedProject)
      setShowDeleteConfirm(false)
      setSelectedProject(null)
    }
  }

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-500/20 border-red-500/30 text-red-400'
      case 'high':
        return 'bg-orange-500/20 border-orange-500/30 text-orange-400'
      case 'medium':
        return 'bg-yellow-500/20 border-yellow-500/30 text-yellow-400'
      default:
        return 'bg-blue-500/20 border-blue-500/30 text-blue-400'
    }
  }

  const getComplianceColor = (status) => {
    switch (status) {
      case 'compliant':
        return 'bg-green-500/20 border-green-500/30'
      case 'partial':
        return 'bg-yellow-500/20 border-yellow-500/30'
      case 'in-progress':
        return 'bg-blue-500/20 border-blue-500/30'
      default:
        return 'bg-red-500/20 border-red-500/30'
    }
  }

  return (
    <div className="space-y-8 pb-20">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Cloud Infrastructure Manager</h1>
          <p className="text-blue-200 mt-1">Security • Compliance • Monitoring • Cost Optimization</p>
        </div>
      </div>

      {/* Risk Score Overview */}
      <div className="grid grid-cols-1 md:grid-cols-7 gap-4">
        <div className="md:col-span-3 bg-gradient-to-br from-slate-800 to-slate-900 rounded-lg border border-blue-500/30 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white">Security Posture</h3>
            <Shield className="text-blue-400" size={24} />
          </div>
          <div className="flex items-center space-x-6">
            <div className="text-center">
              <div className="relative w-24 h-24">
                <svg className="w-24 h-24 transform -rotate-90">
                  <circle
                    cx="48"
                    cy="48"
                    r="40"
                    stroke="#1e293b"
                    strokeWidth="8"
                    fill="none"
                  />
                  <circle
                    cx="48"
                    cy="48"
                    r="40"
                    stroke={riskScore >= 80 ? '#10b981' : riskScore >= 50 ? '#f59e0b' : '#ef4444'}
                    strokeWidth="8"
                    fill="none"
                    strokeDasharray={`${(riskScore / 100) * 251.2} 251.2`}
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className={`text-3xl font-bold ${riskScore >= 80 ? 'text-green-400' : riskScore >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>
                    {riskScore}
                  </span>
                </div>
              </div>
              <p className="text-sm text-blue-200 mt-2">Risk Score</p>
            </div>
            <div className="flex-1 space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs text-blue-200">Critical Issues</p>
                <span className="bg-red-500/20 text-red-400 px-2 py-1 rounded text-sm font-bold">{stats.criticalVulnerabilities}</span>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-xs text-blue-200">Compliance Score</p>
                <span className="bg-green-500/20 text-green-400 px-2 py-1 rounded text-sm font-bold">{stats.complianceScore}%</span>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-xs text-blue-200">Active Threats</p>
                <span className="bg-orange-500/20 text-orange-400 px-2 py-1 rounded text-sm font-bold">{recentThreats.filter(t => t.status !== 'resolved').length}</span>
              </div>
            </div>
          </div>
        </div>

        {/* KPI Stats */}
        <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-blue-200 font-medium">Monthly Cost</p>
            <DollarSign className="text-yellow-600" size={16} />
          </div>
          <p className="text-2xl font-bold text-white">${stats.totalCost.toFixed(0)}</p>
          <p className="text-xs text-blue-300/70 mt-1">{stats.providers} providers</p>
        </div>

        <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-blue-200 font-medium">Instances</p>
            <Server className="text-blue-600" size={16} />
          </div>
          <p className="text-2xl font-bold text-white">{stats.totalInstances}</p>
          <p className="text-xs text-blue-600 mt-1">{stats.runningProjects} running</p>
        </div>

        <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-blue-200 font-medium">Uptime</p>
            <CheckCircle className="text-green-600" size={16} />
          </div>
          <p className="text-2xl font-bold text-white">{stats.avgUptime}%</p>
          <p className="text-xs text-green-600 mt-1">30 days</p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-2 border-b border-blue-500/20 overflow-x-auto">
        {[
          { id: 'overview', label: 'Overview', icon: BarChart3 },
          { id: 'security', label: 'Security Scanning', icon: Shield },
          { id: 'compliance', label: 'Compliance', icon: CheckSquare },
          { id: 'threats', label: 'Threats & Incidents', icon: AlertTriangle },
          { id: 'inventory', label: 'Cloud Inventory', icon: Database },
          { id: 'risk', label: 'Risk Analysis', icon: AlertCircle },
        ].map((tab) => {
          const Icon = tab.icon
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-4 py-3 font-medium text-sm transition-all border-b-2 ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-400'
                  : 'border-transparent text-blue-300 hover:text-blue-200'
              }`}
            >
              <Icon size={16} />
              <span>{tab.label}</span>
            </button>
          )
        })}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Cloud Providers Grid */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Cloud Providers</h2>
              <button
                onClick={handleAddProject}
                className="bg-blue-600 hover:bg-blue-500 text-white font-bold py-2 px-4 rounded-lg flex items-center space-x-2 shadow-lg transition-all"
              >
                <Plus className="w-4 h-4" />
                <span>Add Project</span>
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {cloudProviders.map((provider) => {
                const totalProviderCost = provider.projects.reduce((sum, p) => sum + p.cost, 0)
                const runningProjects = provider.projects.filter((p) => p.status === 'running').length
                return (
                  <button
                    key={provider.id}
                    onClick={() => {
                      setSelectedProvider(provider.id)
                      setSelectedProject(null)
                    }}
                    className={`text-left rounded-lg border-2 p-4 transition-all ${
                      selectedProvider === provider.id
                        ? 'border-blue-500/60 bg-slate-800/70'
                        : 'border-blue-500/30 bg-slate-800/50 hover:border-blue-500/40'
                    }`}
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <span className="text-2xl">{provider.icon}</span>
                      <h3 className="font-bold text-white">{provider.name}</h3>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-slate-700/30 rounded p-2">
                        <p className="text-xs text-blue-200">Cost/Month</p>
                        <p className="font-bold text-white">${totalProviderCost.toFixed(0)}</p>
                      </div>
                      <div className="bg-slate-700/30 rounded p-2">
                        <p className="text-xs text-blue-200">Projects</p>
                        <p className="font-bold text-white">{runningProjects}</p>
                      </div>
                      <div className="bg-slate-700/30 rounded p-2">
                        <p className="text-xs text-blue-200">Instances</p>
                        <p className="font-bold text-white">{provider.projects.reduce((sum, p) => sum + p.instances, 0)}</p>
                      </div>
                      <div className="bg-slate-700/30 rounded p-2">
                        <p className="text-xs text-blue-200">Status</p>
                        <p className="font-bold text-green-600">Online</p>
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Projects List for Selected Provider */}
          {selectedProviderData && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-white">{selectedProviderData.name} - Projects</h2>
                <button
                  onClick={handleAddProject}
                  className="bg-green-600 hover:bg-green-500 text-white font-bold py-2 px-4 rounded-lg flex items-center space-x-2 shadow-lg transition-all"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Project</span>
                </button>
              </div>
              <div className="bg-slate-800/50 rounded-lg border border-blue-500/20 p-3 mb-4">
                <input
                  type="text"
                  placeholder="Search projects..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-700 border border-blue-500/30 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-400"
                />
              </div>
              <div className="grid grid-cols-1 gap-4">
                {filteredProjects.map((project) => (
                  <button
                    key={project.id}
                    onClick={() => setSelectedProject(project.id)}
                    className={`w-full text-left rounded-lg border-2 p-4 transition-all ${
                      selectedProject === project.id
                        ? 'border-green-500/60 bg-slate-800/70'
                        : 'border-blue-500/30 bg-slate-800/50 hover:border-blue-500/40'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-bold text-white">{project.name}</h3>
                      <div className="flex items-center space-x-2">
                        <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-bold">
                          {project.status.toUpperCase()}
                        </span>
                        {selectedProject === project.id && (
                          <div className="flex space-x-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleEditProject()
                              }}
                              className="bg-blue-600 hover:bg-blue-500 text-white p-1.5 rounded transition-colors"
                              title="Edit"
                            >
                              <Edit2 size={14} />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                setShowDeleteConfirm(true)
                              }}
                              className="bg-red-600 hover:bg-red-500 text-white p-1.5 rounded transition-colors"
                              title="Delete"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      <div>
                        <p className="text-xs text-blue-200">Uptime</p>
                        <p className="font-bold text-white">{project.uptime}%</p>
                      </div>
                      <div>
                        <p className="text-xs text-blue-200">CPU Usage</p>
                        <div className="w-full bg-gray-200 rounded h-1.5 mt-1">
                          <div className="bg-red-500 h-1.5 rounded" style={{ width: `${project.resources.cpu}%` }}></div>
                        </div>
                        <p className="text-xs font-bold mt-1">{project.resources.cpu}%</p>
                      </div>
                      <div>
                        <p className="text-xs text-blue-200">Memory</p>
                        <div className="w-full bg-gray-200 rounded h-1.5 mt-1">
                          <div className="bg-orange-500 h-1.5 rounded" style={{ width: `${project.resources.memory}%` }}></div>
                        </div>
                        <p className="text-xs font-bold mt-1">{project.resources.memory}%</p>
                      </div>
                      <div>
                        <p className="text-xs text-blue-200">Storage</p>
                        <div className="w-full bg-gray-200 rounded h-1.5 mt-1">
                          <div className="bg-blue-500 h-1.5 rounded" style={{ width: `${project.resources.storage}%` }}></div>
                        </div>
                        <p className="text-xs font-bold mt-1">{project.resources.storage}%</p>
                      </div>
                      <div>
                        <p className="text-xs text-blue-200">Cost/Month</p>
                        <p className="font-bold text-white">${project.cost.toFixed(0)}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Cost Optimization */}
          {costOptimizations.length > 0 && (
            <div className="bg-slate-800/50 rounded-lg border border-green-500/30 p-6">
              <div className="flex items-center space-x-2 mb-4">
                <DollarSign className="text-green-400" size={24} />
                <h3 className="text-xl font-bold text-white">💰 Cost Optimization Opportunities</h3>
              </div>
              <div className="space-y-3">
                {costOptimizations.slice(0, 5).map((opt, idx) => (
                  <div key={idx} className="bg-slate-700/30 rounded-lg p-4 border border-green-500/20">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-bold text-white">{opt.project}</p>
                        <p className="text-sm text-gray-300 mt-1">{opt.suggestion}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-green-400 font-bold">${opt.potentialSavings.toFixed(0)}</p>
                        <p className="text-xs text-green-300 mt-1">savings/mo</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-green-500/30">
                <p className="text-sm text-green-200">
                  Total Potential Savings:{' '}
                  <span className="font-bold text-lg">
                    ${costOptimizations.reduce((sum, opt) => sum + opt.potentialSavings, 0).toFixed(0)}/month
                  </span>
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Security Scanning Tab */}
      {activeTab === 'security' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-white">Vulnerability Scanning Results</h2>
            <span className="bg-red-500/20 text-red-400 px-3 py-1 rounded text-sm font-bold">{stats.criticalVulnerabilities} Critical</span>
          </div>
          {securityVulnerabilities.map((vuln) => (
            <div key={vuln.id} className={`rounded-lg border-2 p-4 ${getSeverityColor(vuln.severity)}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <AlertCircle size={20} />
                    <h3 className="font-bold text-white">{vuln.name}</h3>
                    <span className="text-xs px-2 py-0.5 bg-slate-700/50 rounded text-white">{vuln.count} found</span>
                  </div>
                  <p className="text-sm text-gray-300 mt-2">Provider: <span className="font-semibold">{vuln.provider}</span></p>
                </div>
                <span className="text-xs font-bold uppercase">{vuln.severity}</span>
              </div>
              <div className="bg-slate-700/30 rounded p-3 mt-2">
                <p className="text-sm text-blue-200"><strong>Recommendation:</strong> {vuln.recommendation}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Compliance Tab */}
      {activeTab === 'compliance' && (
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-white mb-4">Compliance Standards</h2>
          {complianceStatus.map((comp) => (
            <div key={comp.standard} className={`rounded-lg border-2 p-6 ${getComplianceColor(comp.status)}`}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-white">{comp.standard}</h3>
                  <p className="text-sm text-gray-300 mt-1">
                    Last Audit: {comp.lastAudit} • Next: {comp.nextAudit}
                  </p>
                </div>
                <span className={`text-sm font-bold px-3 py-1 rounded uppercase ${
                  comp.status === 'compliant' ? 'bg-green-500/30 text-green-300' :
                  comp.status === 'partial' ? 'bg-yellow-500/30 text-yellow-300' :
                  'bg-blue-500/30 text-blue-300'
                }`}>
                  {comp.status}
                </span>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-blue-200">Compliance Progress</span>
                  <span className="font-bold text-white">{comp.progress}%</span>
                </div>
                <div className="w-full bg-slate-700/50 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${
                      comp.progress === 100 ? 'bg-green-500' :
                      comp.progress >= 75 ? 'bg-yellow-500' :
                      'bg-orange-500'
                    }`}
                    style={{ width: `${comp.progress}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Threats & Incidents Tab */}
      {activeTab === 'threats' && (
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-white mb-4">Real-Time Threat Detection</h2>
          {recentThreats.map((threat) => (
            <div key={threat.id} className={`rounded-lg border-2 p-4 ${getSeverityColor(threat.severity)}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <h3 className="font-bold text-white flex items-center space-x-2">
                    <AlertTriangle size={20} />
                    <span>{threat.type}</span>
                  </h3>
                  <p className="text-sm text-gray-300 mt-2">{threat.source}</p>
                </div>
                <div className="text-right">
                  <span className={`text-xs font-bold px-2 py-1 rounded ${
                    threat.status === 'blocked' ? 'bg-green-500/30 text-green-300' :
                    threat.status === 'quarantined' ? 'bg-yellow-500/30 text-yellow-300' :
                    threat.status === 'investigating' ? 'bg-orange-500/30 text-orange-300' :
                    'bg-blue-500/30 text-blue-300'
                  }`}>
                    {threat.status.toUpperCase()}
                  </span>
                  <p className="text-xs text-gray-400 mt-2">{threat.timestamp}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Cloud Inventory Tab */}
      {activeTab === 'inventory' && (
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-white mb-4">Cloud Resource Inventory</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {cloudInventory.map((resource, idx) => (
              <div key={idx} className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <Database className="text-blue-400" size={24} />
                    <div>
                      <h3 className="font-bold text-white">{resource.type}</h3>
                      <p className="text-xs text-blue-200">{resource.provider}</p>
                    </div>
                  </div>
                  <span className="bg-blue-500/20 text-blue-300 px-3 py-1 rounded font-bold text-sm">{resource.count}</span>
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-blue-500/20">
                  <span className={`text-sm font-semibold ${resource.status === 'running' ? 'text-green-400' : 'text-yellow-400'}`}>
                    {resource.status.charAt(0).toUpperCase() + resource.status.slice(1)}
                  </span>
                  <span className="text-blue-300 font-semibold">{resource.cost}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-4 mt-4">
            <p className="text-sm text-blue-200 mb-2">Total Resources</p>
            <p className="text-3xl font-bold text-white">{cloudInventory.reduce((sum, r) => sum + r.count, 0)}</p>
            <p className="text-xs text-blue-300 mt-1">Across {cloudProviders.length} cloud providers</p>
          </div>
        </div>
      )}

      {/* Risk Analysis Tab */}
      {activeTab === 'risk' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-slate-800/50 rounded-lg border border-orange-500/30 p-6">
              <h3 className="text-lg font-bold text-white mb-4">Risk Factors</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-2 bg-red-500/10 rounded">
                  <span className="text-white">Critical Vulnerabilities</span>
                  <span className="bg-red-500/30 text-red-400 px-2 py-1 rounded font-bold">{securityVulnerabilities.filter(v => v.severity === 'critical').length}</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-orange-500/10 rounded">
                  <span className="text-white">High Risk Issues</span>
                  <span className="bg-orange-500/30 text-orange-400 px-2 py-1 rounded font-bold">{securityVulnerabilities.filter(v => v.severity === 'high').length}</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-yellow-500/10 rounded">
                  <span className="text-white">Non-Compliant Standards</span>
                  <span className="bg-yellow-500/30 text-yellow-400 px-2 py-1 rounded font-bold">{complianceStatus.filter(c => c.status !== 'compliant').length}</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-blue-500/10 rounded">
                  <span className="text-white">Active Threats</span>
                  <span className="bg-blue-500/30 text-blue-400 px-2 py-1 rounded font-bold">{recentThreats.filter(t => t.status !== 'resolved').length}</span>
                </div>
              </div>
            </div>

            <div className="bg-slate-800/50 rounded-lg border border-green-500/30 p-6">
              <h3 className="text-lg font-bold text-white mb-4">Recommendations</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-start space-x-2 text-green-300">
                  <CheckCircle size={16} className="flex-shrink-0 mt-0.5" />
                  <span>Enable encryption on all S3 buckets</span>
                </div>
                <div className="flex items-start space-x-2 text-green-300">
                  <CheckCircle size={16} className="flex-shrink-0 mt-0.5" />
                  <span>Implement Multi-Factor Authentication</span>
                </div>
                <div className="flex items-start space-x-2 text-green-300">
                  <CheckCircle size={16} className="flex-shrink-0 mt-0.5" />
                  <span>Rotate IAM access keys monthly</span>
                </div>
                <div className="flex items-start space-x-2 text-green-300">
                  <CheckCircle size={16} className="flex-shrink-0 mt-0.5" />
                  <span>Close unnecessary security groups</span>
                </div>
                <div className="flex items-start space-x-2 text-green-300">
                  <CheckCircle size={16} className="flex-shrink-0 mt-0.5" />
                  <span>Update outdated OS versions</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-lg border border-blue-500/30 p-6">
            <h3 className="text-lg font-bold text-white mb-4">Overall Security Assessment</h3>
            <p className="text-blue-200 mb-4">
              Your cloud infrastructure has a security risk score of <span className="font-bold text-blue-300">{riskScore}/100</span>.
              {riskScore >= 80 && ' Your security posture is strong. Continue monitoring and applying patches regularly.'}
              {riskScore >= 50 && riskScore < 80 && ' There are several issues that need attention. Prioritize critical vulnerabilities first.'}
              {riskScore < 50 && ' Your infrastructure has significant security gaps. Implement recommendations immediately.'}
            </p>
            <div className="bg-slate-700/50 rounded p-4">
              <p className="text-sm text-gray-300">
                Next steps: Address critical vulnerabilities, enable MFA across all accounts, ensure compliance standards are met, and implement automated threat detection.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Project Modal */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">
                {showEditModal ? 'Edit Project' : 'Add New Project'}
              </h2>
              <button
                onClick={() => {
                  setShowAddModal(false)
                  setShowEditModal(false)
                  setSelectedProject(null)
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-blue-200 mb-2">Project Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-700 border border-blue-500/30 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-400"
                  placeholder="e.g., Production API Server"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-blue-200 mb-2">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-700 border border-blue-500/30 rounded-lg text-white focus:outline-none focus:border-blue-400"
                  >
                    <option value="running">Running</option>
                    <option value="stopped">Stopped</option>
                    <option value="maintenance">Maintenance</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-blue-200 mb-2">Monthly Cost ($)</label>
                  <input
                    type="number"
                    value={formData.cost}
                    onChange={(e) => setFormData({ ...formData, cost: parseFloat(e.target.value) || 0 })}
                    className="w-full px-4 py-2 bg-slate-700 border border-blue-500/30 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-400"
                    placeholder="100"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-blue-200 mb-2">Uptime (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={formData.uptime}
                    onChange={(e) => setFormData({ ...formData, uptime: parseFloat(e.target.value) || 0 })}
                    className="w-full px-4 py-2 bg-slate-700 border border-blue-500/30 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-400"
                    placeholder="99.9"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-blue-200 mb-2">Instances</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.instances}
                    onChange={(e) => setFormData({ ...formData, instances: parseInt(e.target.value) || 1 })}
                    className="w-full px-4 py-2 bg-slate-700 border border-blue-500/30 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-400"
                    placeholder="1"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-blue-200 mb-2">Resources</label>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-gray-400">CPU (%)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={formData.resources.cpu}
                      onChange={(e) => setFormData({
                        ...formData,
                        resources: { ...formData.resources, cpu: parseFloat(e.target.value) || 0 }
                      })}
                      className="w-full px-3 py-1 bg-slate-700 border border-blue-500/30 rounded text-white text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400">Memory (%)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={formData.resources.memory}
                      onChange={(e) => setFormData({
                        ...formData,
                        resources: { ...formData.resources, memory: parseFloat(e.target.value) || 0 }
                      })}
                      className="w-full px-3 py-1 bg-slate-700 border border-blue-500/30 rounded text-white text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400">Storage (%)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={formData.resources.storage}
                      onChange={(e) => setFormData({
                        ...formData,
                        resources: { ...formData.resources, storage: parseFloat(e.target.value) || 0 }
                      })}
                      className="w-full px-3 py-1 bg-slate-700 border border-blue-500/30 rounded text-white text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex space-x-3 mt-8">
              <button
                onClick={() => {
                  setShowAddModal(false)
                  setShowEditModal(false)
                  setSelectedProject(null)
                }}
                className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveProject}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium transition-colors"
              >
                {showEditModal ? 'Update Project' : 'Add Project'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-lg border border-red-500/30 p-6 max-w-sm">
            <h3 className="text-lg font-bold text-white mb-3">Delete Project?</h3>
            <p className="text-gray-300 mb-6">
              Are you sure you want to delete "{selectedProjectData?.name}"? This action cannot be undone.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteProject}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
